import React, { useState, useMemo } from "react";
import { Card, DropdownButton, Flex, Tag, Text } from "@jsq/ds/core";
import { ScreeningMatchResult } from "~/graphql/supergraphEnums";
import { type ScreeningMatchType } from "~/graphql/supergraphTypes";
import { useAmlLuxFeatureFlag } from "~/pages/Compliance/flags";
import { ScreeningMatchUpdatedResults } from "../../../types";
import { mapNumberToPepCategory } from "../utils";
import {
  buttonWithBlackText,
  buttonWithGreenText,
  buttonWithRedText,
  dropDownButton,
  dropDownButtonFalsePositive,
  dropDownButtonTrueMatch,
  evaluationContainer,
  evaluationHeader,
  evaluationLabel,
  evaluationOptions,
  evaluationRow,
  programAffectedLabel,
  programAffectedRow,
  flexBox,
  fullWidth,
  justifyContent,
  pepDropDownButton,
  pepDropDownButtonBlack,
  pepDropDownButtonWithRedBackground,
  resultLabel,
} from "./styles_vanilla.css";

const RESULT_OPTIONS = [
  { value: ScreeningMatchResult.FalsePositive, label: "False positive" },
  { value: ScreeningMatchResult.TrueMatch, label: "True match" },
  { value: ScreeningMatchResult.GatheringInformation, label: "Gathering information" },
];

const PEP_CATEGORY_OPTIONS = [
  { value: "NA", label: "N/A" },
  { value: "pepCategory1", label: "1" },
  { value: "pepCategory2", label: "2" },
  { value: "pepCategory3", label: "3" },
];
interface EvaluationSummaryProps {
  lnMatch: ScreeningMatchType;
  isReadOnly: boolean;
  onResultOptionSelect: (option: string, matchId: number) => void;
  onPEPOptionSelect: (option: string, matchId: number) => void;
  matchResults: ScreeningMatchUpdatedResults[];
}

const EvaluationSummary: React.FC<EvaluationSummaryProps> = ({
  lnMatch,
  isReadOnly,
  onResultOptionSelect,
  onPEPOptionSelect,
  matchResults,
}) => {
  const amlLuxEnabled = useAmlLuxFeatureFlag();
  const matchPrograms = lnMatch.programs;
  // to show the local updated matches when the component unmounts
  const currentUpdatedMatch = matchResults.find((matchResult) => matchResult.id === lnMatch.id);
  const result: string = useMemo(() => {
    if (lnMatch.result !== ScreeningMatchResult.Unknown) return lnMatch.result;
    else if (
      currentUpdatedMatch?.result &&
      currentUpdatedMatch.result !== ScreeningMatchResult.Unknown
    ) {
      return currentUpdatedMatch.result;
    }
    return "";
  }, [lnMatch.result, currentUpdatedMatch?.result]);
  const [selectedResult, setSelectedResult] = useState<string>(result);

  const isValidPepCategoryNumber = (value: number | undefined) => {
    return value === 1 || value === 2 || value === 3;
  };
  const pepCategory: string = useMemo(() => {
    const pepCategoryValue = lnMatch.pepCategory ?? currentUpdatedMatch?.pepCategory;
    if (isValidPepCategoryNumber(pepCategoryValue)) {
      return mapNumberToPepCategory[pepCategoryValue];
    }
    return "";
  }, [lnMatch.pepCategory, currentUpdatedMatch?.pepCategory]);
  const [selectedPepCategory, setSelectedPepCategory] = useState<string>(pepCategory);

  const showNALabel =
    selectedResult === ScreeningMatchResult.FalsePositive ||
    selectedResult === ScreeningMatchResult.GatheringInformation;

  const handleResultSelect = (value: string) => {
    setSelectedResult(value);
    onResultOptionSelect(value, lnMatch.id);
  };

  const handlePepCategorySelect = (value: string) => {
    setSelectedPepCategory(value);
    onPEPOptionSelect(value, lnMatch.id);
  };

  const showIcons = () => {
    switch (selectedResult) {
      case ScreeningMatchResult.FalsePositive:
        return "check";
      case ScreeningMatchResult.TrueMatch:
        return "exclamation-circle";
      case ScreeningMatchResult.GatheringInformation:
        return "sync-alt";
      default:
        return "plus";
    }
  };

  const getButtonClass = () => {
    switch (selectedResult) {
      case ScreeningMatchResult.FalsePositive:
      case ScreeningMatchResult.GatheringInformation:
        return dropDownButtonFalsePositive;
      case ScreeningMatchResult.TrueMatch:
        return dropDownButtonTrueMatch;
      default:
        return dropDownButton;
    }
  };

  const getButtonTextClass = () => {
    switch (selectedResult) {
      case ScreeningMatchResult.TrueMatch:
        return buttonWithRedText;
      case ScreeningMatchResult.FalsePositive:
        return buttonWithGreenText;
      case ScreeningMatchResult.GatheringInformation:
        return buttonWithBlackText;
      default:
        return justifyContent;
    }
  };

  const getPEPButtonClass = () => {
    switch (selectedPepCategory) {
      case "pepCategory1":
      case "pepCategory2":
      case "pepCategory3":
        return pepDropDownButtonWithRedBackground;
      case "NA":
        return pepDropDownButtonBlack;
      default:
        return pepDropDownButton;
    }
  };

  const getOptionLabel = (
    options: { value: string; label: string }[],
    selectedValue: string,
    defaultLabel: string,
  ) => {
    if (!selectedValue) return defaultLabel;

    return options.find((opt) => opt.value === selectedValue)?.label;
  };

  return (
    <Card>
      <div className={evaluationContainer}>
        <Text className={evaluationHeader} variant="secondary-md">
          EVALUATION
        </Text>
        {amlLuxEnabled && matchPrograms.length > 0 && (
          <Flex
            className={programAffectedRow}
            alignItems="flex-start"
            data-testid="compliance-program-affected"
          >
            <Text variant="secondary-md" className={programAffectedLabel}>
              Compliance program affected
            </Text>
            <Flex flexWrap="wrap" gap={4}>
              {matchPrograms.map((program) => (
                <Tag key={program} label={program} />
              ))}
            </Flex>
          </Flex>
        )}
        <Flex className={evaluationOptions} flexDirection="column">
          <Flex className={evaluationRow} flexDirection="row">
            <Text className={resultLabel} variant="regular-md">
              Result
            </Text>
            <DropdownButton
              isFullWidth
              label={getOptionLabel(RESULT_OPTIONS, selectedResult, "Enter Result")}
              align="right"
              variant="link"
              icon={showIcons()}
              disabled={isReadOnly || lnMatch.immutable}
              iconPosition="left"
              className={getButtonClass()}
              buttonClassName={getButtonTextClass()}
              dropdownClassName={fullWidth}
              data-testid="result-dropdown"
            >
              {RESULT_OPTIONS.map((option) => (
                <DropdownButton.Action
                  key={option.value}
                  onSelect={() => handleResultSelect(option.value)}
                >
                  {option.label}
                </DropdownButton.Action>
              ))}
            </DropdownButton>
          </Flex>
          <Flex className={evaluationRow} flexDirection="row">
            <Text className={evaluationLabel} variant="regular-md">
              PEP category
            </Text>
            <DropdownButton
              className={
                selectedResult === ScreeningMatchResult.TrueMatch ? dropDownButton : flexBox
              }
              isFullWidth
              label={
                showNALabel
                  ? "N/A"
                  : getOptionLabel(
                      PEP_CATEGORY_OPTIONS,
                      selectedPepCategory,
                      "Enter PEP category",
                    )
              }
              align="right"
              variant="link"
              icon="plus"
              hideIcon={showNALabel || selectedPepCategory !== ""}
              iconPosition="left"
              buttonClassName={getPEPButtonClass()}
              dropdownClassName={fullWidth}
              disabled={
                isReadOnly ||
                lnMatch.immutable ||
                selectedResult !== ScreeningMatchResult.TrueMatch
              }
              data-testid="pep-category-dropdown"
            >
              {PEP_CATEGORY_OPTIONS.map((option) => (
                <DropdownButton.Action
                  key={option.value}
                  onSelect={() => handlePepCategorySelect(option.value)}
                >
                  {option.label}
                </DropdownButton.Action>
              ))}
            </DropdownButton>
          </Flex>
        </Flex>
      </div>
    </Card>
  );
};
export default EvaluationSummary;
